import { Component } from '@theme/component';

/**
 * Progressive enhancement for the native Winter Arc buy form (snippets/arc-buy-form.liquid).
 *
 * The form posts to /cart/add without JavaScript. This component marks the pressed button
 * as busy so its label swaps for a spinner, and ignores repeat submits while the browser
 * navigates. Busy state is cleared when the page is restored from the back/forward cache.
 *
 * @typedef {{ form?: HTMLFormElement }} ArcBuyFormRefs
 * @extends {Component<ArcBuyFormRefs>}
 */
class ArcBuyForm extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const form = this.#form;
    if (!form) return;

    form.addEventListener('submit', this.#handleSubmit, { signal });
    window.addEventListener('pageshow', this.#reset, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    const { form } = this.refs;
    if (form instanceof HTMLFormElement) return form;

    const fallback = this.querySelector('form');
    return fallback instanceof HTMLFormElement ? fallback : null;
  }

  /**
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    if (this.dataset.busy === 'true') {
      event.preventDefault();
      return;
    }

    this.dataset.busy = 'true';

    if (event.submitter instanceof HTMLButtonElement) {
      event.submitter.setAttribute('aria-busy', 'true');
    }
  };

  #reset = () => {
    delete this.dataset.busy;

    for (const button of this.querySelectorAll('[aria-busy]')) {
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('arc-buy-form')) {
  customElements.define('arc-buy-form', ArcBuyForm);
}
